import React, { useEffect, useMemo, useReducer } from "react";
import U2000InputCurrency from "../../../u2000Core/components/formControls/input/U2000InputCurrency";
import u2000FormState, {
  U2000FormStateEnumEditMode,
  U2000FormStateEnumState,
  type U2000FormState,
} from "../../../u2000Core/reducer/formReducer/U2000FormState";
import { u2000IntFormState, useU2000FormStateHandlers } from "../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { U3125_CURRENCY_FORMAT } from "../../U3125";

type AmountRow = { invoiceAmount: number };

const amountFormInitial = (amount: number): U2000FormState<AmountRow> => ({
  ...u2000IntFormState,
  current: { invoiceAmount: amount },
  old: { invoiceAmount: amount },
  state: U2000FormStateEnumState.EDIT,
  editMode: U2000FormStateEnumEditMode.EDIT,
  touched: {},
  errors: {},
  otherErrors: {},
  isSubmitted: false,
  customAttributes: {},
});

export type U3125ForfaitWizardInvoiceAmountInputProps = {
  value: number;
  onChange: (amount: number) => void;
  disabled?: boolean;
  maxValue?: number;
};

/** Saisie montant facture wizard — wrapper U2000InputCurrency pour cellule de grille. */
const U3125ForfaitWizardInvoiceAmountInput: React.FC<U3125ForfaitWizardInvoiceAmountInputProps> = ({
  value,
  onChange,
  disabled = false,
  maxValue,
}) => {
  const [formState, formStateDispatch] = useReducer(u2000FormState, undefined, () => amountFormInitial(value));
  const baseHandlers = useU2000FormStateHandlers<AmountRow>({
    formState,
    formStateDispatch,
    businessHandlers: {
      newRecord: () => ({ invoiceAmount: 0 }),
    },
  });

  useEffect(() => {
    const current = baseHandlers.getData()?.invoiceAmount;
    if (current !== value) {
      baseHandlers.setData({ invoiceAmount: value });
    }
    // Resync externe uniquement quand la valeur parent change (ex. re-preview).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const formStateHandlers = useMemo(
    () => ({
      ...baseHandlers,
      onChange: (e: React.ChangeEvent<HTMLInputElement>, next: string | number | boolean | null) => {
        baseHandlers.onChange?.(e, next as string | number | boolean | any[]);
        const num = typeof next === "number" && Number.isFinite(next) ? next : Number(next) || 0;
        onChange(num);
      },
    }),
    [baseHandlers, onChange],
  );

  return (
    <div className="min-w-36">
      <U2000InputCurrency
        formStateHandlers={formStateHandlers}
        fieldName="invoiceAmount"
        label=""
        disabled={disabled}
        decimalDigits={U3125_CURRENCY_FORMAT.decimalDigits}
        currencySymbol={U3125_CURRENCY_FORMAT.currencySymbol}
        symbolPosition={U3125_CURRENCY_FORMAT.symbolPosition}
        decimalSeparator={U3125_CURRENCY_FORMAT.decimalSeparator}
        thousandSeparator={U3125_CURRENCY_FORMAT.thousandSeparator}
        allowNegative={false}
        maxValue={maxValue}
      />
    </div>
  );
};

export default U3125ForfaitWizardInvoiceAmountInput;
